import { BoundaryCharacteristicSIDefinitions } from '@cadstrom/sigma-engine-shared';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import type { ExpressionDetails } from '@cadstrom/sigma-engine-shared';

interface ExpressionDetailsProps {
	details: ExpressionDetails | null | undefined;
	fallbackValue?: string | null;
}

/**
 * Component for rendering ExpressionDetails with expression, evaluated value, and variables
 * Shows the expression below the value for expressions with variables, and a tooltip with detailed information
 */
export function ExpressionDetailsComponent({ details, fallbackValue }: ExpressionDetailsProps) {
	// If no details available, show fallback value
	if (!details) {
		return <span>{fallbackValue ?? '—'}</span>;
	}

	const { expression, evaluatedValue, variables, characteristic } = details;
	const characteristicKey = characteristic.toLowerCase();
	const unit =
		characteristicKey in BoundaryCharacteristicSIDefinitions ?
			BoundaryCharacteristicSIDefinitions[characteristicKey as keyof typeof BoundaryCharacteristicSIDefinitions].short
		:	'';

	// Format the evaluated value with units
	const formatValue = (value: number | null | undefined) => {
		if (value === undefined || value === null) {
			return '—';
		}
		return `${value.toPrecision(3)}${unit}`;
	};

	// Format range if available
	const formatRange = (min?: number | null, max?: number | null) => {
		if (min !== undefined && min !== null && max !== undefined && max !== null) {
			return `${min.toPrecision(3)} to ${max.toPrecision(3)}${unit}`;
		}
		if (min !== undefined && min !== null) {
			return `≥ ${min.toPrecision(3)}${unit}`;
		}
		if (max !== undefined && max !== null) {
			return `≤ ${max.toPrecision(3)}${unit}`;
		}
		return null;
	};

	// Main display value - handle all combinations of value, min, and max
	const displayValue = (() => {
		const hasValue = evaluatedValue?.value !== undefined && evaluatedValue.value !== null;
		const hasMin = evaluatedValue?.allowableRange.min !== undefined && evaluatedValue.allowableRange.min !== null;
		const hasMax = evaluatedValue?.allowableRange.max !== undefined && evaluatedValue.allowableRange.max !== null;

		// If we have a main value, show it with optional range in parentheses
		if (hasValue) {
			const mainValue = formatValue(evaluatedValue.value);
			const rangeText = formatRange(evaluatedValue.allowableRange.min, evaluatedValue.allowableRange.max);
			return rangeText !== null ? `${mainValue} (${rangeText})` : mainValue;
		}

		// If no main value but we have range information, show the range
		if (hasMin || hasMax) {
			const rangeText = formatRange(evaluatedValue.allowableRange.min, evaluatedValue.allowableRange.max);
			return rangeText ?? fallbackValue ?? '—';
		}

		return fallbackValue ?? '—';
	})();

	// Function to replace variable names with original names in brackets
	const formatExpressionWithOriginalNames = (expr: string, vars: typeof variables) => {
		if (!vars || vars.length === 0) {
			return expr;
		}

		let formattedExpression = expr;

		// Sort variables by length (longest first) to avoid partial replacements
		const sortedVars = [...vars].sort((a, b) => b.variablename.length - a.variablename.length);

		for (const variable of sortedVars) {
			const originalName = variable.originalName?.trim() ?? '';
			if (originalName !== '') {
				// Use word boundaries to ensure we only replace complete variable names
				const regex = new RegExp(`\\b${variable.variablename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
				formattedExpression = formattedExpression.replace(regex, `(${originalName})`);
			}
		}

		return formattedExpression;
	};

	// Check if we have variables to determine if we should show expression below
	const hasVariables = variables && variables.length > 0;

	// If we have variables, show the expression below the value with tooltip
	if (hasVariables) {
		// Tooltip content with detailed information for expressions with variables
		const tooltipContent = (
			<div className='max-w-sm space-y-3'>
				<div className='space-y-2'>
					<Typography variant='sm' className='text-gray-200 font-medium'>
						Variables:
					</Typography>
					<div className='space-y-1.5 pl-2'>
						{variables.map((variable) => (
							<div key={variable.variablename} className='text-gray-300 text-xs'>
								<span className='text-gray-200 font-mono'>
									{(() => {
										const originalName = variable.originalName?.trim() ?? '';
										return originalName !== '' ? originalName : variable.variablename;
									})()}
									:
								</span>{' '}
								<span className='text-gray-100'>
									{formatValue(variable.value.value)}
									{(() => {
										const rangeText = formatRange(variable.value.allowableRange.min, variable.value.allowableRange.max);
										return rangeText !== null ? <span className='text-gray-400 ml-2'>({rangeText})</span> : null;
									})()}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>
		);

		const valueElement = (
			<Tooltip
				title={tooltipContent}
				placement='top'
				enterDelay={500}
				classes={{
					tooltip: 'shadow-lg max-w-none rounded p-3',
				}}
			>
				<span className='decoration-gray-400 cursor-help underline decoration-dotted'>{displayValue}</span>
			</Tooltip>
		);

		return (
			<div className='flex flex-col py-2'>
				{valueElement}
				<Typography variant='xs' className='text-gray-600 mt-1 py-2 font-mono leading-tight break-all'>
					{formatExpressionWithOriginalNames(expression, variables)}
				</Typography>
			</div>
		);
	}

	// If no variables, just show the plain evaluated value without any popup
	return <span>{displayValue}</span>;
}
